import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Save, Delete, Add, ExpandMore } from '@mui/icons-material';

const GameConfig = () => {
  const [config, setConfig] = useState({
    trustingThreshold: 10,
    obedientThreshold: 20,
    devotedThreshold: 30,
    startingTokens: 100,
    introFlowId: null,
    fusionRequirements: []
  });
  const [flows, setFlows] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const [cfgRes, flowsRes, charsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/game-config`),
        fetch(`${API_BASE_URL}/api/flows/summary`),
        fetch(`${API_BASE_URL}/api/characters`)
      ]);

      if (flowsRes.ok) {
        const flowData = await flowsRes.json();
        setFlows(Array.isArray(flowData) ? flowData : []);
      } else {
        setFlows([]);
      }

      if (charsRes.ok) {
        const charsData = await charsRes.json();
        setCharacters(Array.isArray(charsData) ? charsData : []);
      } else {
        setCharacters([]);
      }

      if (cfgRes.ok) {
        const data = await cfgRes.json();
        setConfig((prev) => ({
          ...prev,
          ...data,
          introFlowId: data.introFlowId ?? null,
          fusionRequirements: Array.isArray(data.fusionRequirements) ? data.fusionRequirements : []
        }));
      } else {
        throw new Error('Failed to load game config');
      }
    } catch (error) {
      console.error('Error loading game config:', error);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/game-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error('Failed to save game config');
      }
    } catch (error) {
      console.error('Error saving game config:', error);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    const numValue = parseInt(value, 10) || 0;
    setConfig((prev) => ({
      ...prev,
      [field]: numValue
    }));
  };

  const introFlowMissing =
    config.introFlowId != null &&
    config.introFlowId !== '' &&
    !flows.some((f) => String(f._id) === String(config.introFlowId));

  const introSelectValue =
    config.introFlowId != null && config.introFlowId !== '' ? String(config.introFlowId) : '';

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 0 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 0 }}>
              Configuration saved successfully.
            </Alert>
          )}

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Relationship level thresholds
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Trusting threshold"
                  type="number"
                  value={config.trustingThreshold}
                  onChange={(e) => handleChange('trustingThreshold', e.target.value)}
                  helperText="Relationship points required to reach Trusting"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  fullWidth
                  label="Obedient threshold"
                  type="number"
                  value={config.obedientThreshold}
                  onChange={(e) => handleChange('obedientThreshold', e.target.value)}
                  helperText="Relationship points required to reach Obedient"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  fullWidth
                  label="Devoted threshold"
                  type="number"
                  value={config.devotedThreshold}
                  onChange={(e) => handleChange('devotedThreshold', e.target.value)}
                  helperText="Relationship points required to reach Devoted"
                  inputProps={{ min: 0 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Economy
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                label="Starting tokens"
                type="number"
                value={config.startingTokens}
                onChange={(e) => handleChange('startingTokens', e.target.value)}
                helperText="Tokens granted when starting the game"
                inputProps={{ min: 0 }}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Flows
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small">
                <InputLabel id="game-config-intro-flow-label">Intro Flow</InputLabel>
                <Select
                  labelId="game-config-intro-flow-label"
                  label="Intro Flow"
                  value={introSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    setConfig((prev) => ({
                      ...prev,
                      introFlowId: v === '' ? null : v
                    }));
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {introFlowMissing ? (
                    <MenuItem value={String(config.introFlowId)} disabled>
                      (flow not found — choose another or clear)
                    </MenuItem>
                  ) : null}
                  {flows.map((f) => (
                    <MenuItem key={f._id} value={String(f._id)}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Dialogue flow shown when the player starts the game.
                </FormHelperText>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Fusion Room
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fusion Event Date & Time"
                  type="datetime-local"
                  value={config.fusionEventDate ? new Date(config.fusionEventDate).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setConfig((prev) => ({
                      ...prev,
                      fusionEventDate: v ? new Date(v).toISOString() : null
                    }));
                  }}
                  helperText="Countdown target for Fusion Room. Leave empty for 'Coming Soon'."
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
              Participation Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Characters and relationship levels required to participate in a Fusion session.
            </Typography>
            {(config.fusionRequirements || []).map((req, idx) => (
              <Grid container spacing={2} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Character</InputLabel>
                    <Select
                      label="Character"
                      value={req.characterId ? String(req.characterId) : ''}
                      onChange={(e) => {
                        const updated = [...config.fusionRequirements];
                        updated[idx] = { ...updated[idx], characterId: e.target.value || null };
                        setConfig((prev) => ({ ...prev, fusionRequirements: updated }));
                      }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {characters.map((c) => (
                        <MenuItem key={c._id} value={String(c._id)}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Relationship Level</InputLabel>
                    <Select
                      label="Relationship Level"
                      value={req.relationshipLevel || 'trusting'}
                      onChange={(e) => {
                        const updated = [...config.fusionRequirements];
                        updated[idx] = { ...updated[idx], relationshipLevel: e.target.value };
                        setConfig((prev) => ({ ...prev, fusionRequirements: updated }));
                      }}
                    >
                      <MenuItem value="neutral">Neutral</MenuItem>
                      <MenuItem value="trusting">Trusting</MenuItem>
                      <MenuItem value="obedient">Obedient</MenuItem>
                      <MenuItem value="devoted">Devoted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const updated = config.fusionRequirements.filter((_, i) => i !== idx);
                      setConfig((prev) => ({ ...prev, fusionRequirements: updated }));
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                setConfig((prev) => ({
                  ...prev,
                  fusionRequirements: [...(prev.fusionRequirements || []), { characterId: null, relationshipLevel: 'trusting' }]
                }));
              }}
              sx={{ mt: 1 }}
            >
              Add Requirement
            </Button>
            </AccordionDetails>
          </Accordion>

          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<Save />}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GameConfig;
